import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Grid, Card, IconButton, Tooltip, CircularProgress, Box, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider, Stack, Snackbar, Alert, TextField, Popper, ClickAwayListener } from "@mui/material";
import { Visibility, Download, CloudDownload, ArrowBack, Description, PictureAsPdf, Delete } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { useQuery } from "@tanstack/react-query";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import { IconTableExport } from "@tabler/icons-react";
import handleExport from "utils/exportData";
import PropTypes from "prop-types";

function ClaimsDetails() {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const location = useLocation(); // To get state passed from list view (e.g. user name)
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [exportData, setExportData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [message, setMessage] = useState({
    status: "",
    title: "",
    msg: "",
  });
  const [openMonthPicker, setOpenMonthPicker] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, url: null, category: null, claimId: null });
  const [deleting, setDeleting] = useState(false);

  const handleClosePdf = () => setViewPdfUrl(null);

  // Use passed state for user details if available, otherwise fetch/infer
  const [userDetails, setUserDetails] = useState(location.state || { user_name: "User", district_name: "" });

  const [filters, setFilters] = useState({
    fromMonth: location.state?.fromMonth ? dayjs(location.state.fromMonth) : dayjs().startOf('month'),
    toMonth: location.state?.toMonth ? dayjs(location.state.toMonth) : dayjs().endOf('month'),
  });

  // Generate month options
  const monthOptions = useMemo(() => {
    const years = [dayjs().year() - 1, dayjs().year(), dayjs().year() + 1];
    const months = [];
    years.forEach(year => {
      for (let m = 0; m < 12; m++) {
        const date = dayjs().year(year).month(m);
        if (!date.isAfter(dayjs(), 'month')) {
          months.push({ label: date.format("MMM YYYY"), value: date.format("YYYY-MM") });
        }
      }
    });
    return months.reverse();
  }, []);

  // Fetch current user (Admin check)
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });

  const fetchUserClaims = async (page = 0, size = 10, filterValues = filters, colFilters = columnFilters) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: page + 1,
        limit: size,
        from_month: filterValues.fromMonth ? filterValues.fromMonth.format("YYYY-MM") : "",
        to_month: filterValues.toMonth ? filterValues.toMonth.format("YYYY-MM") : "",
        columnFilters: JSON.stringify(colFilters),
      });

      // Fetch specific user's claims
      // Ideally backend should support /claims/list/:user_id with filters
      // If we use the admin endpoint, we might get everything. 
      // Reuse the endpoint logic: /claims/list/:user_id

      const url = `${process.env.REACT_APP_API_URL}/claims/list/${user_id}?${queryParams.toString()}`;

      const res = await axios.get(url, { withCredentials: true });
      if (res.data.status === 200) {
        // The API returns data array. Check structure.
        // Based on user request, regular user claims list returns direct claims array?
        // Or if it returns the grouped structure, we extract claims.
        // Assuming /claims/list/:id returns the claims for that user.
        // If it returns [{ user_id, claims: [...] }], we need to extract.

        let fetchedData = res.data.data || [];

        // If the API returns the grouped structure even for single user ID (which is possible if the endpoint is shared)
        if (fetchedData.length > 0 && fetchedData[0].claims) {
          // For safety, find the one matching user_id if multiple
          const userRec = fetchedData.find(u => String(u.user_id) === String(user_id));
          if (userRec) {
            // Update user details if not set
            if (!userDetails.district_name) {
              setUserDetails({ user_name: userRec.user_name, district_name: userRec.district_name });
            }
            // Add user_name and district_name to each claim
            fetchedData = (userRec.claims || []).map(claim => ({
              ...claim,
              user_name: userRec.user_name,
              district_name: userRec.district_name
            }));
          } else {
            fetchedData = [];
          }
        } else {
          // If data is already claims array, add user details to each
          fetchedData = fetchedData.map(claim => ({
            ...claim,
            user_name: claim.user_name || userDetails.user_name,
            district_name: claim.district_name || userDetails.district_name
          }));
        }

        setData(fetchedData);
        setRowCount(res.data.totalCount || fetchedData.length); // Adjust if totalCount is for all users
        setExportData(fetchedData);
      }
    } catch (err) {
      console.error("Error fetching claims:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user_id) {
      fetchUserClaims(pagination.pageIndex, pagination.pageSize, filters, columnFilters);
    }
  }, [pagination.pageIndex, pagination.pageSize, filters, columnFilters, sorting, user_id]);

  const handleDownload = async (url, title) => {
    if (!url) return;
    try {
      const cleanUrl = url.split('?')[0].toLowerCase();
      const originalExt = cleanUrl.split('.').pop();
      // Only download extensions that are not pdf
      // If it is a word doc, download as is.
      // Actually we just want to download the file directly with its extension.

      let downloadExt = originalExt;
      if (originalExt.startsWith('doc')) {
        downloadExt = originalExt;
      }

      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${title.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.${downloadExt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setMessage({ status: "success", title: "Success", msg: "File downloaded successfully!" });
    } catch (error) {
      console.error("Download failed", error);
      setMessage({ status: "error", title: "Error", msg: "Download failed. Please try again." });
    }
  };

  const parseDocUrls = (docData) => {
    if (!docData) return [];
    let urls = [];
    try {
      if (typeof docData === 'string') {
        if (docData.startsWith('[') && docData.endsWith(']')) {
          urls = JSON.parse(docData);
        } else {
          urls = [docData];
        }
      } else if (Array.isArray(docData)) {
        urls = docData;
      }
    } catch (e) {
      urls = [docData];
    }
    return urls;
  };

  const handlePreview = (url) => {
    if (!url) return;
    const cleanUrl = url.split('?')[0].toLowerCase();
    const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');

    if (isWord) {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      setViewPdfUrl(officeUrl);
    } else {
      setViewPdfUrl(url);
    }
  };

  const handleDeleteFile = async () => {
    if (!deleteDialog.url || !deleteDialog.claimId) return;
    
    setDeleting(true);
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/claims/delete-file/${deleteDialog.claimId}`,
        {
          data: { url: deleteDialog.url, category: deleteDialog.category },
          withCredentials: true
        }
      );

      if (res.data.status === 200) {
        setMessage({ status: "success", title: "Success", msg: "File deleted successfully!" });
        // Refresh data
        fetchUserClaims(pagination.pageIndex, pagination.pageSize, filters, columnFilters);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      setMessage({ 
        status: "error", 
        title: "Error", 
        msg: error.response?.data?.message || "Failed to delete file." 
      });
    } finally {
      setDeleting(false);
      setDeleteDialog({ open: false, url: null, category: null, claimId: null });
    }
  };

  const MRTDocumentCell = ({ cell, column, row }) => {
    const value = cell.getValue();
    const title = column.columnDef.header;
    const urls = parseDocUrls(value);
    const claimId = row.original.id;

    if (!urls || urls.length === 0) return <MDTypography variant="caption" color="text">-</MDTypography>;

    const isAdmin = meData?.role_id === 1 || meData?.role_id === 2;

    return (
      <MDBox display="flex" flexDirection="column" gap={0.5} py={0.5}>
        {urls.map((url, index) => {
          const cleanUrl = url.split('?')[0].toLowerCase();
          const isPdf = cleanUrl.endsWith('.pdf');
          const isWord = cleanUrl.endsWith('.doc') || cleanUrl.endsWith('.docx');

          return (
            <MDBox key={index} display="flex" gap={1} alignItems="center">
              {isPdf ? <PictureAsPdf sx={{ fontSize: '1rem', color: '#f44336' }} /> : (isWord ? <Description sx={{ fontSize: '1rem', color: '#1976d2' }} /> : null)}
              <Tooltip title={`View Document ${urls.length > 1 ? index + 1 : ''}`}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={() => handlePreview(url)}
                  sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' }, width: 24, height: 24 }}
                >
                  <Visibility sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={`Download Document ${urls.length > 1 ? index + 1 : ''}`}>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => handleDownload(url, title)}
                  sx={{ bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' }, width: 24, height: 24 }}
                >
                  <CloudDownload sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Delete File">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteDialog({ open: true, url, category: title, claimId })}
                    sx={{ bgcolor: '#ffebee', '&:hover': { bgcolor: '#ffcdd2' }, width: 24, height: 24 }}
                  >
                    <Delete sx={{ fontSize: '0.875rem' }} />
                  </IconButton>
                </Tooltip>
              )}
            </MDBox>
          );
        })}
      </MDBox>
    );
  };

  MRTDocumentCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
    column: PropTypes.shape({
      columnDef: PropTypes.shape({
        header: PropTypes.string.isRequired
      }).isRequired
    }).isRequired,
  };

  const MonthCell = ({ cell }) => {
    const val = cell.getValue();
    if (val) return dayjs(val, "YYYY-MM").format("MMM YYYY");
    if (filters.fromMonth) return filters.fromMonth.format("MMM YYYY");
    return "-";
  };

  MonthCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const DateCell = ({ cell }) => {
    return cell.getValue() ? dayjs(cell.getValue()).format("DD-MM-YYYY") : "-";
  };

  DateCell.propTypes = {
    cell: PropTypes.shape({
      getValue: PropTypes.func.isRequired,
    }).isRequired,
  };

  const columns = useMemo(() => {
    // Extract unique options from data
    const userOptions = [
      ...new Set(data.map((item) => item.user_name || userDetails.user_name).filter(Boolean)),
    ];
    const districtOptions = [
      ...new Set(
        data.map((item) => item.district_name || userDetails.district_name).filter(Boolean)
      ),
    ];

    return [
      {
        accessorKey: "user_name",
        header: "User Name",
        size: 150,
        filterVariant: "select",
        filterSelectOptions: userOptions,
      },
      {
        accessorKey: "district_name",
        header: "District",
        size: 150,
        filterVariant: "select",
        filterSelectOptions: districtOptions,
      },
      {
        accessorKey: "month",
        header: "Claim Month",
        size: 160,
        filterVariant: "select",
        filterSelectOptions: monthOptions.map((o) => o.value),
        Cell: MonthCell,
      },
      {
        accessorKey: "created_at",
        header: "Submitted On",
        size: 200,
        Cell: DateCell,
      },
      {
        accessorKey: "attendance_sheet",
        header: "Attendance Sheet",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: MRTDocumentCell,
      },
      {
        accessorKey: "stipend_claim",
        header: "Stipend Claim",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: MRTDocumentCell,
      },
      {
        accessorKey: "travel_bills",
        header: "Travel Bills",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: MRTDocumentCell,
      },
      {
        accessorKey: "review_ta_bills",
        header: "Review TA Bills",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: MRTDocumentCell,
      },
    ];
  }, [filters, monthOptions, data, userDetails]);

  const handleExportData = async () => {
    try {
      if (data.length === 0) {
        setMessage({ status: "error", title: "Error", msg: "No data found to export." });
        return;
      }

      await handleExport(data, "Claims_Records");
      setMessage({ status: "success", title: "Success", msg: "Excel downloaded successfully!" });
    } catch (error) {
      console.error("Export error:", error);
      setMessage({ status: "error", title: "Error", msg: "Something went wrong during export." });
    }
  };

  return (
    <DashboardLayout>
      <MDBox mt={3} />
      <Card sx={{ mb: 2 }}>
        <MDBox pt={2} px={2} display="flex" justifyContent="space-between" alignItems="center" pb={2}>
          <MDBox display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: "#344767" }}>
              <ArrowBack />
            </IconButton>
            <MDTypography variant="h5" fontWeight="medium">
              Claims Details: {userDetails.user_name}
            </MDTypography>
          </MDBox>
          <MDTypography variant="subtitle2" color="text">
            {userDetails.district_name}
          </MDTypography>
        </MDBox>
      </Card>
      {/* <MDBox px={2} py={2}>
          <Grid container spacing={3}>
            <Grid item size={{ xs: 12, md: 4 }}>
              <MDTypography variant="caption" fontWeight="bold">Month Range</MDTypography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="From"
                    views={['year', 'month']}
                    value={filters.fromMonth}
                    onChange={(val) => setFilters(f => ({ ...f, fromMonth: val }))}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                  <DatePicker
                    label="To"
                    views={['year', 'month']}
                    value={filters.toMonth}
                    onChange={(val) => setFilters(f => ({ ...f, toMonth: val }))}
                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  />
                </LocalizationProvider>
              </Box>
            </Grid>
          </Grid>
        </MDBox> */}
      {/* </Card> */}

      <Card>
        <MaterialReactTable
          columns={columns}
          data={data}
          getRowId={(row) => row.id}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          initialState={{ showColumnFilters: false }}
          layoutMode="grid"
          enableRowNumbers
          enableStickyHeader
          manualFiltering
          manualPagination
          manualSorting
          rowCount={rowCount}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          onColumnFiltersChange={setColumnFilters}
          state={{ isLoading: loading, pagination, sorting, columnFilters }}
          muiTableHeadProps={{
            sx: {
              padding: "0px",
              backgroundColor: "#011F3A",
            },
          }}
          enableColumnActions={false}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#004030",
              color: "#fff",
              fontWeight: "bold",
              position: "sticky",
              top: 0,
              zIndex: 1,
              "& .MuiTableSortLabel-root": {
                "& .MuiTableSortLabel-icon": {
                  color: "#fff !important",
                },
              },
              "& .MuiInputBase-input": {
                color: "#fff !important",
                fontSize: "12px",
                "::placeholder": {
                  color: "#e0e0e0",
                  opacity: 1,
                },
              },
              "& .MuiInputBase-root.Mui-focused .MuiInputBase-input::placeholder": {
                color: "#ffffff",
              },
              "& .MuiIconButton-root": {
                color: "#fff",
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#e0e0e0",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff",
              },
              "& .MuiCheckbox-root": {
                color: "#fff",
              },
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              padding: "4px 8px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              backgroundColor: row.index % 2 === 0 ? "#fafafa" : "#f0f0f0",
            },
          })}
          renderTopToolbarCustomActions={({ table }) => (
            <Box
              sx={{
                display: "flex",
                gap: "1rem",
                p: "0.5rem",
                flexWrap: "wrap",
                marginLeft: "auto",
              }}
            >
              <Tooltip title={"Export data to excel sheet"} arrow>
                <Button
                  onClick={handleExportData}
                  sx={{
                    background:
                      "linear-gradient(90deg, rgba(2, 183, 255, 0.65) 30%,rgb(110, 199, 235) 100%)",
                    color: "#f4f4f4",
                    borderRadius: "8px",
                    padding: "10px 20px",
                    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
                    transition: "background 0.3s, transform 0.3s",
                    "&:hover": {
                      background:
                        "linear-gradient(90deg, rgba(55, 198, 254, 0.97) 30%,rgb(39, 169, 220) 100%))",
                      transform: "scale(1.05)",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                  startIcon={<IconTableExport />}
                >
                  {"Export To Excel"}
                </Button>
              </Tooltip>
            </Box>
          )}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: "#fff",
              boxShadow: "none",
            },
          }}
        />
      </Card>

      {/* PDF Viewer Dialog */}
      <Dialog open={Boolean(viewPdfUrl)} onClose={handleClosePdf} maxWidth="lg" fullWidth>
        <DialogTitle display="flex" justifyContent="space-between">
          Document Preview{" "}
          <IconButton onClick={handleClosePdf}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "60vh" }}>
          {viewPdfUrl && (
            <iframe
              src={viewPdfUrl}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="PDF"
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={Boolean(message.status)}
        autoHideDuration={3000}
        onClose={() => setMessage({ status: "", title: "", msg: "" })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setMessage({ status: "", title: "", msg: "" })}
          severity={message.status || "success"}
          variant="filled"
          sx={{ width: "100%", color: "white !important" }}
        >
          {message.msg}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ ...deleteDialog, open: false })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text">
            Are you sure you want to delete this file? This action will remove the file from the claim and Vercel storage.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton 
            onClick={() => setDeleteDialog({ ...deleteDialog, open: false })} 
            color="dark" 
            disabled={deleting}
          >
            Cancel
          </MDButton>
          <MDButton 
            onClick={handleDeleteFile} 
            color="error" 
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Confirm Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout >
  );
}

export default ClaimsDetails;
